import type { LatLng } from '../components/shopper/DeliveryMap';

export interface GeocodableAddress {
  street?: string;
  line1?: string;
  unit?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  zip?: string;
  lat?: number | null;
  lng?: number | null;
}

const cache = new Map<string, LatLng | null>();

/** Build a single-line address suitable for Google Geocoding. */
export function formatAddressLine(addr?: GeocodableAddress | null): string {
  if (!addr) return '';
  const street = [addr.street || addr.line1, addr.unit].filter(Boolean).join(' ');
  return [street, addr.city, addr.state, addr.zipCode || addr.zip].filter(Boolean).join(', ');
}

export function coordsFromAddress(addr?: GeocodableAddress | null): LatLng | null {
  if (!addr) return null;
  if (typeof addr.lat === 'number' && typeof addr.lng === 'number') {
    return { lat: addr.lat, lng: addr.lng };
  }
  return null;
}

/** Firestore GeoPoint uses latitude/longitude; some docs use lat/lng objects. */
export function coordsFromFirestoreLocation(loc?: {
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
} | null): LatLng | null {
  if (!loc) return null;
  if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
    return { lat: loc.lat, lng: loc.lng };
  }
  if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
    return { lat: loc.latitude, lng: loc.longitude };
  }
  return null;
}

export interface ReverseGeocoded {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

/** Turn coordinates into a street address. Requires Google Maps to be loaded. */
export async function reverseGeocode(point: LatLng): Promise<ReverseGeocoded | null> {
  if (!window.google?.maps?.Geocoder) return null;

  const geocoder = new google.maps.Geocoder();
  try {
    const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
      geocoder.geocode({ location: point }, (res, status) => {
        if (status === 'OK' && res?.length) resolve(res);
        else reject(new Error(status));
      });
    });

    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zipCode = '';

    for (const comp of results[0].address_components) {
      const types = comp.types;
      if (types.includes('street_number')) streetNumber = comp.long_name;
      else if (types.includes('route')) route = comp.long_name;
      else if (types.includes('locality')) city = comp.long_name;
      else if (types.includes('sublocality_level_1') && !city) city = comp.long_name;
      else if (types.includes('administrative_area_level_1')) state = comp.short_name;
      else if (types.includes('postal_code')) zipCode = comp.long_name;
    }

    return {
      street: [streetNumber, route].filter(Boolean).join(' '),
      city,
      state,
      zipCode,
    };
  } catch {
    return null;
  }
}

/** Geocode an address once Google Maps is loaded. Returns cached results. */
export async function geocodeAddress(addressLine: string): Promise<LatLng | null> {
  const key = addressLine.trim().toLowerCase();
  if (!key) return null;
  if (cache.has(key)) return cache.get(key)!;

  if (!window.google?.maps?.Geocoder) {
    return null;
  }

  const geocoder = new google.maps.Geocoder();
  try {
    const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
      geocoder.geocode({ address: addressLine }, (results, status) => {
        if (status === 'OK' && results?.length) resolve(results);
        else reject(new Error(status));
      });
    });
    const loc = result[0].geometry.location;
    const point = { lat: loc.lat(), lng: loc.lng() };
    cache.set(key, point);
    return point;
  } catch {
    cache.set(key, null);
    return null;
  }
}
